import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Globe, 
  Search, 
  Star, 
  Phone, 
  CheckCircle, 
  ArrowRight,
  Rocket,
  TrendingUp,
  Users,
  MessageSquare,
  Smartphone,
  BarChart3,
  Shield,
  Zap,
  X,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const BOOKING_URL = "https://api.forcesystems.io/widget/booking/MY9TsUsbO2gkyDu286JV";

const AddOnServices = () => {
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Load the booking script when modal opens
  useEffect(() => {
    if (showBookingModal) {
      const script = document.createElement('script');
      script.src = 'https://api.forcesystems.io/js/form_embed.js';
      script.type = 'text/javascript';
      document.body.appendChild(script);
      
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [showBookingModal]);

  const features = [
    {
      icon: Globe,
      title: 'Professional Website Design',
      description: 'Custom-built, mobile-optimized website that converts visitors into leads. Showcases your work and builds trust.'
    },
    {
      icon: Search,
      title: 'Local SEO Optimization',
      description: 'Rank higher on Google for roofing searches in your area. Stop paying for unqualified leads from directories.'
    },
    {
      icon: Star,
      title: '5-Star Review Funnel',
      description: 'Automated system to collect and showcase your best reviews. Filter negative reviews before they go public.'
    },
    {
      icon: Phone,
      title: 'Missed Call Text-Back',
      description: 'Never lose a lead. Automatic text response when you miss a call, keeping the conversation going.'
    },
    {
      icon: MessageSquare,
      title: 'Google Business Optimization',
      description: 'Fully optimized Google Business Profile to dominate local search and maps results.'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Design',
      description: '87% of customers view your site on mobile. We ensure a perfect experience on every device.'
    }
  ];

  const stats = [
    { value: '154%', label: 'Average Client Growth' },
    { value: '5000+', label: 'Leads Generated' },
    { value: '127+', label: 'Projects Completed' },
    { value: '112+', label: '5-Star Reviews' }
  ];

  const testimonials = [
    {
      quote: "The website they built looks incredible, and the systems they put in place have made handling leads effortless.",
      author: "Mark",
      company: "Summit Exterior Washing"
    },
    {
      quote: "I've been blown away by the quality of work. My online presence looks better than ever, and I'm already seeing more inbound leads.",
      author: "Daniel",
      company: "BrightLine Electrical"
    },
    {
      quote: "Professional, responsive, and delivered exactly what was promised. A game-changer for my business.",
      author: "Jake",
      company: "Precision Paint Co"
    }
  ];

  return (
    <div className="p-6 space-y-8 max-w-6xl mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full -ml-32 -mb-32"></div>
        
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <Rocket className="w-8 h-8" />
            <span className="text-lg font-semibold bg-white/20 px-4 py-1 rounded-full">Premium Add-On</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Marketing & Website Services
          </h1>
          
          <p className="text-xl text-purple-100 mb-8 max-w-2xl">
            Get more roofing leads with a professional website, local SEO, and automated marketing systems. 
            We handle your online presence while you focus on what you do best.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-4 text-center">
              <p className="text-3xl font-bold">$1,000</p>
              <p className="text-purple-200">One-time Setup</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-4 text-center">
              <p className="text-3xl font-bold">$400<span className="text-lg">/mo</span></p>
              <p className="text-purple-200">Maintenance</p>
            </div>
          </div>
          
          <Button 
            onClick={() => setShowBookingModal(true)}
            className="bg-white text-purple-700 hover:bg-purple-50 font-semibold px-8 py-6 text-lg"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book Your Free Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className="text-center">
            <CardContent className="p-6">
              <p className="text-3xl font-bold text-purple-600">{stat.value}</p>
              <p className="text-gray-600 text-sm">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* What's Included */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-green-600" />
          What's Included
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* How It Works */}
      <Card className="bg-gray-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-500" />
            Simple 3-Step Process
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
              <h3 className="font-semibold mb-2">Demo Call (20 min)</h3>
              <p className="text-gray-600 text-sm">We show you live client accounts and answer all your questions.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
              <h3 className="font-semibold mb-2">We Build (7-10 days)</h3>
              <p className="text-gray-600 text-sm">Full website build out with all systems customized for your business.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
              <h3 className="font-semibold mb-2">Launch (30 min)</h3>
              <p className="text-gray-600 text-sm">We walk you through everything and you can make unlimited changes.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testimonials */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Star className="w-6 h-6 text-yellow-500" />
          What Contractors Say
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-gray-500 text-sm">{testimonial.company}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <Card className="border-2 border-purple-200 bg-purple-50">
        <CardContent className="p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Grow Your Home Service Business?
          </h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Join hundreds of contractors who have transformed their online presence. 
            Get started today with a free consultation call.
          </p>
          <Button 
            onClick={() => setShowBookingModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-6 text-lg"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Schedule Your Free Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {/* Booking Calendar Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">Book Your Free Consultation</h2>
              </div>
              <button 
                onClick={() => setShowBookingModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="p-4" style={{ height: '600px' }}>
              <iframe 
                src={BOOKING_URL}
                style={{ width: '100%', height: '100%', border: 'none', overflow: 'hidden' }}
                scrolling="no"
                title="Book a Consultation"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOnServices;
